import type { ValidationQuickFillFocus } from '../../../shared/types/validationNavigation.types';

interface QuickFillScopeElementLike {
  elementId?: string;
  elementName?: string;
  familyName?: string;
  typeName?: string;
}

interface QuickFillScopeFamilyLike<TType extends { name?: string }> {
  name?: string;
  types?: TType[];
}

function stripQuickFillRowDisplaySuffix(value: string): string {
  return value
    .trim()
    .replace(/\s*\(ID:?\s*[A-Za-z0-9_-]+\)\s*$/i, '')
    .replace(/\s*\[\d+\]\s*$/g, '')
    .trim();
}

function normalizeQuickFillRowToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s*:\s*/g, ':')
    .replace(/\s+/g, ' ');
}

function parseQuickFillRowName(value: string): { familyName: string; typeName: string | null } {
  const normalizedValue = stripQuickFillRowDisplaySuffix(value);
  const separatorIndex = normalizedValue.indexOf(':');
  if (separatorIndex < 0) {
    return {
      familyName: normalizedValue.trim(),
      typeName: null,
    };
  }

  return {
    familyName: normalizedValue.slice(0, separatorIndex).trim(),
    typeName: normalizedValue.slice(separatorIndex + 1).trim() || null,
  };
}

function normalizeQuickFillFamilyTypeKey(value: string): string {
  return normalizeQuickFillRowToken(stripQuickFillRowDisplaySuffix(value));
}

function hasExplicitElementScope(focus: Pick<ValidationQuickFillFocus, 'elementIds'>): boolean {
  return focus.elementIds.some((elementId) => elementId.trim() !== '');
}

function buildQuickFillFamilyTypeKey(familyName: string | undefined, typeName: string | undefined): string {
  const normalizedFamilyName = stripQuickFillRowDisplaySuffix(familyName || '').trim();
  const normalizedTypeName = stripQuickFillRowDisplaySuffix(typeName || '').trim();
  if (!normalizedFamilyName) {
    return '';
  }

  return normalizedTypeName ? `${normalizedFamilyName}:${normalizedTypeName}` : normalizedFamilyName;
}

function matchesScopedFamilyTypeKey(candidateKey: string, scopedKey: string): boolean {
  if (!candidateKey || !scopedKey) {
    return false;
  }

  if (scopedKey.includes(':')) {
    return candidateKey === scopedKey;
  }

  return candidateKey === scopedKey || candidateKey.startsWith(`${scopedKey}:`);
}

export function filterQuickFillElementsByFocus<T extends QuickFillScopeElementLike>(
  elements: T[],
  focus: ValidationQuickFillFocus | null | undefined
): T[] {
  if (!focus) {
    return elements;
  }

  const normalizedElementIds = new Set(
    focus.elementIds.map((elementId) => elementId.trim()).filter(Boolean)
  );
  if (normalizedElementIds.size > 0) {
    const exactMatches = elements.filter((element) =>
      typeof element.elementId === 'string' && normalizedElementIds.has(element.elementId.trim())
    );
    return exactMatches;
  }

  const normalizedFamilyTypeKeys = Array.from(
    new Set(focus.familyTypeKeys.map((key) => normalizeQuickFillFamilyTypeKey(key)).filter(Boolean))
  );
  if (normalizedFamilyTypeKeys.length > 0) {
    const familyTypeMatches = elements.filter((element) => {
      const candidateKey = normalizeQuickFillFamilyTypeKey(
        buildQuickFillFamilyTypeKey(element.familyName, element.typeName)
      );
      return normalizedFamilyTypeKeys.some((scopedKey) =>
        matchesScopedFamilyTypeKey(candidateKey, scopedKey)
      );
    });
    if (familyTypeMatches.length > 0) {
      return familyTypeMatches;
    }
  }

  const normalizedRowCandidates = Array.from(
    new Set(
      focus.rowCandidates.map((candidate) => normalizeQuickFillFamilyTypeKey(candidate)).filter(Boolean)
    )
  );
  if (normalizedRowCandidates.length > 0) {
    const rowCandidateMatches = elements.filter((element) => {
      const candidateRowName = normalizeQuickFillFamilyTypeKey(
        buildQuickFillFamilyTypeKey(element.familyName, element.typeName)
      );
      const candidateElementName = normalizeQuickFillFamilyTypeKey(element.elementName || '');

      return normalizedRowCandidates.some((scopedKey) =>
        matchesScopedFamilyTypeKey(candidateRowName, scopedKey) ||
        matchesScopedFamilyTypeKey(candidateElementName, scopedKey)
      );
    });
    if (rowCandidateMatches.length > 0) {
      return rowCandidateMatches;
    }
  }

  return elements;
}

export function filterQuickFillFamiliesByFocus<
  TType extends { name?: string },
  TFamily extends QuickFillScopeFamilyLike<TType>
>(
  families: TFamily[],
  focus: ValidationQuickFillFocus | null | undefined,
  scopedElements: QuickFillScopeElementLike[] = [],
  sourceElementsAvailable = false
): TFamily[] {
  if (!focus) {
    return families;
  }

  if (sourceElementsAvailable && hasExplicitElementScope(focus) && scopedElements.length === 0) {
    return [];
  }

  const scopedElementKeys = Array.from(
    new Set(
      scopedElements
        .map((element) => normalizeQuickFillFamilyTypeKey(buildQuickFillFamilyTypeKey(element.familyName, element.typeName)))
        .filter(Boolean)
    )
  );
  const normalizedFamilyTypeKeys = Array.from(
    new Set(
      (scopedElementKeys.length > 0 ? scopedElementKeys : focus.familyTypeKeys)
        .map((key) => normalizeQuickFillFamilyTypeKey(key))
        .filter(Boolean)
    )
  );

  if (normalizedFamilyTypeKeys.length === 0) {
    return families;
  }

  const scopedFamilies = families.reduce<TFamily[]>((result, family) => {
    const familyName = stripQuickFillRowDisplaySuffix(family.name || '').trim();
    if (!familyName) {
      return result;
    }

    const normalizedFamilyKey = normalizeQuickFillFamilyTypeKey(familyName);
    const matchingScopedKeys = normalizedFamilyTypeKeys.filter((scopedKey) =>
      matchesScopedFamilyTypeKey(normalizedFamilyKey, scopedKey) ||
      scopedKey.startsWith(`${normalizedFamilyKey}:`)
    );

    if (matchingScopedKeys.length === 0) {
      return result;
    }

    const familyTypes = Array.isArray(family.types) ? family.types : [];
    if (familyTypes.length === 0) {
      result.push(family);
      return result;
    }

    const filteredTypes = familyTypes.filter((typeItem) => {
      const candidateKey = normalizeQuickFillFamilyTypeKey(
        buildQuickFillFamilyTypeKey(familyName, typeItem.name)
      );
      return matchingScopedKeys.some((scopedKey) => matchesScopedFamilyTypeKey(candidateKey, scopedKey));
    });

    result.push({
      ...family,
      types: filteredTypes.length > 0 ? filteredTypes : familyTypes,
    });
    return result;
  }, []);

  return scopedFamilies.length > 0 ? scopedFamilies : families;
}
